import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { ClockIcon } from '../../components/ui/Icons';
import { WhatsAppIcon } from '../../components/ui/ContactIcons';
import { formatCurrency } from '../../utils/currency';
import { buildCartInquiryWhatsAppUrl } from '../../utils/whatsapp';
import { useStickyCtaOffset } from '../../hooks/useStickyCtaOffset';
import { CHECKOUT_PRIMARY_ACTION_CLASS } from '../checkout/styles';
import { FOCUS_RING, LINK_BUTTON_GHOST } from './cartStyles';

interface CartSummaryProps {
  productsTotal: number;
  pixPrice: number | null;
  reservationText: string | null;
  catalogHref: string;
  ctaDisabled: boolean;
  blockReason: string | null;
  onContinue: () => void;
}

export function CartSummary({
  productsTotal,
  pixPrice,
  reservationText,
  catalogHref,
  ctaDisabled,
  blockReason,
  onContinue,
}: CartSummaryProps) {
  return (
    <section aria-label="Resumo da sacola" className="bg-champagne border border-champagne-dark rounded-xl p-4 sm:p-5 shadow-sm">
      <div className="flex flex-col gap-1.5 text-sm text-text-dark">
        <div className="flex justify-between items-center">
          <span>Subtotal</span>
          <span className="font-semibold">R$ {formatCurrency(productsTotal)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span>Frete</span>
          <span className="font-semibold text-right">A vendedora confirma pelo WhatsApp</span>
        </div>
      </div>

      <div className="border-t border-dashed border-chocolate/40 mt-3 pt-3">
        <div className="flex justify-between items-center font-bold text-text-dark text-lg">
          <span>Total dos produtos</span>
          <span className="text-chocolate-dark">R$ {formatCurrency(productsTotal)}</span>
        </div>
        <p className="text-xs text-text-medium mt-0.5">
          + frete, que a vendedora confirma com você pelo WhatsApp depois que você enviar o pedido
        </p>
      </div>

      {pixPrice !== null && (
        <p className="mt-3 inline-block px-3 py-1 rounded-full bg-olivine-light text-text-dark text-sm font-semibold">
          No Pix: R$ {formatCurrency(pixPrice)}
        </p>
      )}

      {reservationText && (
        <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-white/70 px-3 py-2 text-xs text-text-dark">
          <ClockIcon className="w-4 h-4 shrink-0 mt-0.5 text-chocolate-dark" />
          {reservationText}
        </p>
      )}

      <div className="hidden lg:block mt-4">
        {blockReason && (
          <p role="status" className="text-sm text-text-dark mb-2">
            {blockReason}
          </p>
        )}
        <Button
          fullWidth
          size="lg"
          onClick={onContinue}
          disabled={ctaDisabled}
          className={`${CHECKOUT_PRIMARY_ACTION_CLASS} ${FOCUS_RING}`}
        >
          Continuar
        </Button>
      </div>

      <Link to={catalogHref} className={`${LINK_BUTTON_GHOST} w-full mt-3 hover:!bg-white/70`}>
        Continuar comprando
      </Link>

      <a
        href={buildCartInquiryWhatsAppUrl()}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-1 inline-flex items-center justify-center gap-2 w-full min-h-11 rounded-lg text-sm font-medium text-text-medium hover:text-chocolate transition-colors ${FOCUS_RING}`}
      >
        <WhatsAppIcon className="w-4 h-4 text-olivine-dark shrink-0" />
        Dúvidas? Fale no WhatsApp
      </a>
    </section>
  );
}

interface CartMobileBarProps {
  productsTotal: number;
  ctaDisabled: boolean;
  blockReason: string | null;
  onContinue: () => void;
}

export function CartMobileBar({ productsTotal, ctaDisabled, blockReason, onContinue }: CartMobileBarProps) {
  const stickyCtaRef = useStickyCtaOffset<HTMLDivElement>();

  return (
    <div ref={stickyCtaRef} className="lg:hidden fixed inset-x-0 bottom-consent z-sticky-cta bg-white border-t border-champagne-dark px-4 pt-3 pb-safe shadow-[0_-8px_20px_-16px_rgba(60,36,21,0.3)]">
      {blockReason && (
        <p role="status" className="text-sm text-text-dark mb-2">
          {blockReason}
        </p>
      )}
      <div className="flex items-center gap-3">
        <div className="shrink-0 leading-tight">
          <span className="block text-xs text-text-medium">Total dos produtos</span>
          <span className="text-xl font-extrabold text-chocolate-dark">R$ {formatCurrency(productsTotal)}</span>
          <span className="block text-xs text-text-medium">+ frete pelo WhatsApp</span>
        </div>
        <Button
          size="lg"
          onClick={onContinue}
          disabled={ctaDisabled}
          className={`flex-1 min-w-0 min-h-12 ${CHECKOUT_PRIMARY_ACTION_CLASS} ${FOCUS_RING}`}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
